import SiteCoreLinks from "@/components/SiteCoreLinks";
import { HOME_FAQS as faqs } from "@/data/home-core";
import { useState, useMemo, useRef, useEffect } from "react";
import { InternalSiteMap } from "@/components/InternalSiteMap";
import { LazyHydrate } from "@/components/LazyHydrate";
// VIDEOS + videoObjectNode intentionally not imported — see note in head() below.

import {
  Phone,
  Mail,
  Globe,
  MessageCircle,
  Award,
  CheckCircle2,
  Scale,
  Stamp,
  FileText,
  Plane,
  Briefcase,
  ShieldCheck,
  MapPin,
  Star,
  Menu,
  X,
  ExternalLink,
  Building2,
  Clock,
  Sparkles,
  GraduationCap,
  Users,
  Languages,
  BadgeCheck,
  Truck,
  Zap,
  Globe2,
  Baby,
  HeartHandshake,
  FileCheck2,
  Landmark,
  ScrollText,
  Car,
  Home,
  IdCard,
  Search,
  ArrowRight,
  TrendingUp,
  Activity,
  Headphones,
  Banknote,
  Send,
  PenTool,
  ChevronDown,
} from "lucide-react";
import heroEarth from "@/assets/hero-earth.webp";
import heroEarth640Avif from "@/assets/hero-earth-640.avif";
import heroEarth1280Avif from "@/assets/hero-earth-1280.avif";
import heroEarth1920Avif from "@/assets/hero-earth-1920.avif";
import heroEarth1280Webp from "@/assets/hero-earth-1280.webp";
import heroEarth1920Webp from "@/assets/hero-earth-1920.webp";
import heroTeamShowcase from "@/assets/hero-team-showcase.jpg";
import nycLogo from "@/assets/nyc-logo-small.webp";

import { credentials } from "@/data/credentials";
import { NearMeWidget } from "@/components/NearMeWidget";

const SITE_URL = "https://nyclanguageinstitute.com";

/* ---------- DATA ---------- */

const departments = [
  {
    tag: "Legal",
    icon: FileText,
    title: "Translation & Legalization",
    desc: "แปลและรับรองเอกสารราชการ ส่งกงสุล/สถานทูตทุกประเทศ ครบจบในที่เดียว",
    startPrice: "฿500",
    line: "@NYCLI",
    lineUrl: "https://line.me/ti/p/@nycli",
    email: "nycli@ilc.ltd",
    site: "www.nyclanguageinstitute.com",
    siteUrl: "https://www.nyclanguageinstitute.com/",
  },
  {
    tag: "Legal",
    icon: Stamp,
    title: "Notary Public",
    desc: "รับรองลายมือชื่อและเอกสารโดยทนายผู้ได้รับใบอนุญาตจากสภาทนายความ 6 ท่าน",
    startPrice: "฿1,500",
    line: "@INOTARY",
    lineUrl: "https://line.me/ti/p/@inotary",
    email: "notary@ilc.ltd",
    site: "www.nycnotarypublicthailand.com",
    siteUrl: "https://www.nycnotarypublicthailand.com/",
  },
  {
    tag: "Legal",
    icon: Scale,
    title: "NAATI Australia",
    desc: "แปลและรับรองโดยนักแปล NAATI สำหรับวีซ่า/สัญชาติ/มหาวิทยาลัยออสเตรเลีย",
    startPrice: "฿1,200",
    line: "@NAATI",
    lineUrl: "https://line.me/ti/p/@naati",
    email: "naati@ilc.ltd",
    site: "www.naati.me",
    siteUrl: "https://www.naati.me/",
  },
  {
    tag: "Legal",
    icon: ShieldCheck,
    title: "Police Clearance",
    desc: "บริการดำเนินการขอใบตรวจประวัติอาชญากรรม Royal Thai Police CID แบบเร่งด่วน",
    startPrice: "฿1,500",
    line: "@317ybmlc",
    lineUrl: "https://line.me/ti/p/@317ybmlc",
    email: "cid@ilc.ltd",
    site: "ตรวจประวัติอาชญากรรม.com",
    siteUrl: "https://www.xn--12cngm1bb5b0clcea7bd5gzbsuc9a.com/",
  },
  {
    tag: "Visa",
    icon: Plane,
    title: "Visa for Thai People",
    desc: "บริการขอวีซ่า Schengen / USA / UK / Australia / Canada สำหรับคนไทยและครอบครัว",
    startPrice: "Quote",
    line: "@NYCV",
    lineUrl: "https://line.me/ti/p/@nycv",
    email: "contact@nycvisa.org",
    site: "www.nycvisa.org",
    siteUrl: "https://www.nycvisa.org/",
  },
  {
    tag: "Visa",
    icon: Briefcase,
    title: "Foreigner Visa & Work Permit",
    desc: "Business / Marriage / Retirement Visa และ Work Permit สำหรับชาวต่างชาติในไทย",
    startPrice: "Quote",
    line: "@NYCT",
    lineUrl: "https://line.me/ti/p/@nyct",
    email: "contact@nycvisa.org",
    site: "www.nycvisa.org",
    siteUrl: "https://www.nycvisa.org/",
  },
];

const attorneys = [
  { th: "นางสาวอนุตรี", en: "Miss Anutaree", license: "942/2554", since: "2554" },
  { th: "นายจิรพันธ์", en: "Mr. Jirapan", license: "1112/2551", since: "2551" },
  { th: "นายจิรศักดิ์", en: "Mr. Jirasak", license: "4759/2565", since: "2565" },
  { th: "นายปฏิภาณ", en: "Mr. Patipan", license: "1114/2558", since: "2558" },
  { th: "นายวราวุธ", en: "Mr. Warawut", license: "3041/2565", since: "2565" },
  { th: "นายวิวัฒน์", en: "Mr. Wiwat", license: "931/2551", since: "2551" },
];

const pricing = {
  translation: [
    { name: "เอกสารทั่วไป (General)", price: "500 - 2,500 ฿", unit: "/หน้า", note: "จดหมาย, บทความ, เอกสารธุรกิจทั่วไป" },
    { name: "เอกสารราชการ (Official)", price: "500 - 1,500 ฿", unit: "/หน้า", note: "สูติบัตร, ทะเบียนบ้าน, บัตรประชาชน, ใบสำคัญสมรส" },
    { name: "เอกสารเฉพาะทาง (Technical)", price: "1,000 - 2,500 ฿", unit: "/หน้า", note: "คู่มือแพทย์, สัญญากฎหมาย, งบการเงิน, วิศวกรรม" },
    { name: "นักแปลผู้เชี่ยวชาญศาล", price: "1,800 - 3,000 ฿", unit: "/ฉบับ", note: "Sworn Translator สำหรับยื่นศาล/หน่วยงานเฉพาะ" },
  ],
  notary: [
    { name: "Notary Public Attorney", price: "1,500 ฿", unit: "/ตรา", note: "รับรองลายมือชื่อ, สำเนาถูกต้อง, รับรองตัวบุคคล" },
    { name: "NAATI Translator (Australia)", price: "1,200 - 2,000 ฿", unit: "/หน้า", note: "แปลและรับรอง NAATI พร้อมจัดส่งทั่วโลก" },
    { name: "ค่าธรรมเนียมกงสุล", price: "200 - 800 ฿", unit: "/ฉบับ", note: "ค่าธรรมเนียมตามจริงของกรมการกงสุล" },
    { name: "ค่าบริการยื่นกงสุล", price: "2,000 - 5,000 ฿", unit: "/ออเดอร์", note: "เดินเรื่อง, รับ-ส่ง, ติดตามผลครบวงจร" },
    { name: "ค่าธรรมเนียมสถานทูต", price: "1,000 - 2,500 ฿", unit: "/ฉบับ", note: "ตามอัตราของแต่ละสถานทูต" },
    { name: "ค่าบริการยื่นสถานทูต", price: "4,000 - 8,000 ฿", unit: "/ออเดอร์", note: "บริการยื่นรับรองนิติกรณ์สถานทูต" },
  ],
  background: [
    { name: "หนังสือรับรองความประพฤติ (Personal)", price: "1,500 - 2,500 ฿", unit: "/คน", note: "CID Police Clearance สำหรับคนไทย" },
    { name: "หนังสือรับรองความประพฤติ (Visa)", price: "4,000 - 5,000 ฿", unit: "/คน", note: "สำหรับยื่นวีซ่าต่างประเทศ" },
    { name: "ค่าจัดส่งในประเทศ (EMS)", price: "100 ฿", unit: "/ครั้ง", note: "EMS / Kerry Express ทั่วไทย" },
    { name: "ค่าจัดส่งต่างประเทศ (DHL)", price: "2,500 ฿+", unit: "เริ่มต้น", note: "ตามน้ำหนักและประเทศปลายทาง" },
  ],
};

const branches = [
  { tag: "Headquarters", name: "Bangkok HQ", addr: "61 Soi Ladprao 95, Wang Thonglang, Bangkok 10310", street: "61 Soi Ladprao 95 (Prangthip), Wang Thonglang", locality: "Bangkok", region: "Bangkok", postalCode: "10310" },
  { tag: "Regional Hub", name: "Khon Kaen", addr: "95 The Wall, Ruen Rom Rd, Muang, Khon Kaen", street: "95 The Wall, Ruen Rom Road, Mueang", locality: "Khon Kaen", region: "Khon Kaen", postalCode: "40000" },
  { tag: "Regional Hub", name: "Udon Thani", addr: "31/43 Sri Chom Chuen Rd, Mak Khaeng, Udon Thani", street: "31/43 Sri Chom Chuen Road, Mak Khaeng, Mueang", locality: "Udon Thani", region: "Udon Thani", postalCode: "41000" },
  { tag: "Border Point", name: "Nong Khai", addr: "161 Moo 1, Pa Kho, Mueang Nong Khai", street: "161 Moo 1, Pa Kho, Mueang", locality: "Nong Khai", region: "Nong Khai", postalCode: "43000" },
];

const testimonials = [
  { name: "Sompong K.", text: "บริการดีมากครับ แปลเอกสารเร็วและถูกต้อง รับรองกงสุลผ่านฉลุย", service: "Legalization" },
  { name: "Jessica M.", text: "Professional service. The staff was very helpful with my visa documents.", service: "Visa" },
  { name: "Praphat S.", text: "ประทับใจความรวดเร็วครับ ส่งงานตรงเวลา ราคาไม่แพง", service: "Translation" },
  { name: "Warataya J.", text: "แนะนำเลยค่ะ ใครหาที่แปลเอกสารราชการ ที่นี่มืออาชีพมากๆ", service: "Translation" },
  { name: "Michael R.", text: "Great experience. Needed certified translation for embassy, they handled it perfectly.", service: "Legalization" },
  { name: "Nattapong T.", text: "บริการดี ให้คำปรึกษาดีมากครับ ไม่เข้าใจตรงไหนถามได้ตลอด", service: "Notary" },
  { name: "Siriporn P.", text: "แปลเอกสารด่วนได้ทันใจมากค่ะ คุณภาพงานดี ไม่มีที่ติ", service: "Translation" },
  { name: "David L.", text: "Highly recommended for notary services in Bangkok.", service: "Notary" },
  { name: "Kanda W.", text: "เจ้าหน้าที่พูดจาดี บริการรวดเร็ว ประทับใจค่ะ", service: "Translation" },
  { name: "Thanawat B.", text: "ราคามาตรฐาน งานคุณภาพ สะดวกมากมีสาขาใกล้บ้าน", service: "Legalization" },
  { name: "Sarah J.", text: "Fast and reliable translation service. Will use again.", service: "NAATI" },
  { name: "Chaiwat N.", text: "แปลเอกสารกฎหมายได้แม่นยำมากครับ ทนายเซ็นรับรองถูกต้อง", service: "Notary" },
  { name: "Pimchanok S.", text: "บริการครบวงจรจริงๆ ค่ะ ทั้งแปล ทั้งรับรอง ไม่ต้องวิ่งหลายที่", service: "Legalization" },
  { name: "Robert K.", text: "Excellent service for business document translation.", service: "Translation" },
  { name: "Emily W.", text: "Very efficient and friendly staff. Good English communication.", service: "NAATI" },
  { name: "Lisa T.", text: "Best translation service I've found in Thailand.", service: "Translation" },
];


const documentCategories = [
  {
    id: "gov",
    label: "เอกสารราชการ",
    icon: Landmark,
    items: [
      { icon: Baby, label: "สูติบัตร" },
      { icon: Home, label: "ทะเบียนบ้าน" },
      { icon: IdCard, label: "บัตรประชาชน" },
      { icon: HeartHandshake, label: "ใบสำคัญสมรส" },
      { icon: ScrollText, label: "ใบเปลี่ยนชื่อ-สกุล" },
      { icon: FileCheck2, label: "ใบหย่า" },
      { icon: BadgeCheck, label: "ทะเบียนรับรองบุตร" },
      { icon: FileText, label: "หนังสือมรณบัตร" },
    ],
    price: "เริ่ม ฿500/หน้า",
    eta: "1-2 วันทำการ",
  },
  {
    id: "edu",
    label: "เอกสารการศึกษา",
    icon: GraduationCap,
    items: [
      { icon: ScrollText, label: "Transcript" },
      { icon: BadgeCheck, label: "ใบปริญญาบัตร" },
      { icon: FileCheck2, label: "ใบรับรองการศึกษา" },
      { icon: Award, label: "ใบประกาศนียบัตร" },
      { icon: FileText, label: "Course Description" },
      { icon: Sparkles, label: "Recommendation Letter" },
    ],
    price: "เริ่ม ฿600/หน้า",
    eta: "2-3 วันทำการ",
  },
  {
    id: "biz",
    label: "เอกสารธุรกิจ",
    icon: Building2,
    items: [
      { icon: Building2, label: "หนังสือรับรองบริษัท" },
      { icon: Banknote, label: "งบการเงิน" },
      { icon: ScrollText, label: "สัญญาธุรกิจ" },
      { icon: FileText, label: "ภพ.20 / VAT" },
      { icon: BadgeCheck, label: "Power of Attorney" },
      { icon: FileCheck2, label: "บอจ. / บอจ.5" },
    ],
    price: "เริ่ม ฿800/หน้า",
    eta: "2-4 วันทำการ",
  },
  {
    id: "personal",
    label: "เอกสารส่วนบุคคล",
    icon: Users,
    items: [
      { icon: Plane, label: "พาสปอร์ต" },
      { icon: Car, label: "ใบขับขี่" },
      { icon: ShieldCheck, label: "Police Clearance" },
      { icon: FileText, label: "Medical Certificate" },
      { icon: Briefcase, label: "ใบรับรองการทำงาน" },
      { icon: Banknote, label: "Bank Statement" },
    ],
    price: "เริ่ม ฿500/หน้า",
    eta: "1-3 วันทำการ",
  },
];

const languagesByRegion = [
  { region: "Asia", icon: Globe2, langs: ["จีน (Mandarin)", "ญี่ปุ่น", "เกาหลี", "ฮินดี", "เบงกาลี", "พม่า", "ลาว", "เขมร", "เวียดนาม", "อินโดนีเซีย", "มาเลย์", "ฟิลิปปินส์ (ตากาล็อก)", "เนปาล", "ทมิฬ", "สิงหล", "อูรดู"] },
  { region: "Europe", icon: Globe, langs: ["อังกฤษ", "ฝรั่งเศส", "เยอรมัน", "สเปน", "อิตาลี", "โปรตุเกส", "ดัตช์", "รัสเซีย", "โปแลนด์", "เช็ก", "สวีดิช", "นอร์เวย์", "เดนิช", "ฟินแลนด์", "ฮังการี", "กรีก"] },
  { region: "Middle East", icon: Star, langs: ["อาหรับ", "เปอร์เซีย (Farsi)", "ฮิบรู", "ตุรกี", "เคิร์ด", "อัฟกัน (Pashto)"] },
  { region: "Americas & Africa", icon: Plane, langs: ["สเปน (Latin)", "โปรตุเกส (Brazil)", "ฝรั่งเศส (Quebec)", "Swahili", "Amharic", "Hausa", "Zulu"] },
];

const bkkDistricts = [
  "พระนคร","ดุสิต","หนองจอก","บางรัก","บางเขน","บางกะปิ","ปทุมวัน","ป้อมปราบศัตรูพ่าย",
  "พระโขนง","มีนบุรี","ลาดกระบัง","ยานนาวา","สัมพันธวงศ์","พญาไท","ธนบุรี","บางกอกใหญ่",
  "ห้วยขวาง","คลองสาน","ตลิ่งชัน","บางกอกน้อย","บางขุนเทียน","ภาษีเจริญ","หนองแขม",
  "ราษฎร์บูรณะ","บางพลัด","ดินแดง","บึงกุ่ม","สาทร","บางซื่อ","จตุจักร","บางคอแหลม","ประเวศ",
  "คลองเตย","สวนหลวง","จอมทอง","ดอนเมือง","ราชเทวี","ลาดพร้าว","วัฒนา","บางแค",
  "หลักสี่","สายไหม","คันนายาว","สะพานสูง","วังทองหลาง","คลองสามวา","บางนา","ทวีวัฒนา",
  "ทุ่งครุ","บางบอน",
];

const provincesAll = [
  "กรุงเทพมหานคร","กระบี่","กาญจนบุรี","กาฬสินธุ์","กำแพงเพชร","ขอนแก่น","จันทบุรี","ฉะเชิงเทรา",
  "ชลบุรี","ชัยนาท","ชัยภูมิ","ชุมพร","เชียงราย","เชียงใหม่","ตรัง","ตราด","ตาก","นครนายก",
  "นครปฐม","นครพนม","นครราชสีมา","นครศรีธรรมราช","นครสวรรค์","นนทบุรี","นราธิวาส","น่าน",
  "บึงกาฬ","บุรีรัมย์","ปทุมธานี","ประจวบคีรีขันธ์","ปราจีนบุรี","ปัตตานี","พระนครศรีอยุธยา","พังงา",
  "พัทลุง","พิจิตร","พิษณุโลก","เพชรบุรี","เพชรบูรณ์","แพร่","พะเยา","ภูเก็ต","มหาสารคาม",
  "มุกดาหาร","แม่ฮ่องสอน","ยโสธร","ยะลา","ร้อยเอ็ด","ระนอง","ระยอง","ราชบุรี","ลพบุรี",
  "ลำปาง","ลำพูน","เลย","ศรีสะเกษ","สกลนคร","สงขลา","สตูล","สมุทรปราการ","สมุทรสงคราม",
  "สมุทรสาคร","สระแก้ว","สระบุรี","สิงห์บุรี","สุโขทัย","สุพรรณบุรี","สุราษฎร์ธานี","สุรินทร์",
  "หนองคาย","หนองบัวลำภู","อ่างทอง","อำนาจเจริญ","อุดรธานี","อุตรดิตถ์","อุทัยธานี","อุบลราชธานี",
];

const workflowSteps = [
  { icon: Send, title: "ส่งเอกสาร", desc: "ส่งสำเนา/สแกนทาง LINE @NYCLI หรืออีเมล nycli@ilc.ltd", time: "1 นาที" },
  { icon: Zap, title: "ใบเสนอราคา", desc: "ทีมงานประเมินราคาและระยะเวลา ตอบกลับภายใน 15 นาที", time: "15 นาที" },
  { icon: Banknote, title: "ชำระเงิน", desc: "โอน / พร้อมเพย์ / บัตรเครดิต พร้อมใบกำกับภาษี VAT", time: "ทันที" },
  { icon: PenTool, title: "แปล + รับรอง", desc: "นักแปล + ทนาย Notary + กงสุล/สถานทูตตามต้องการ", time: "1-5 วัน" },
  { icon: Truck, title: "จัดส่งทั่วโลก", desc: "รับที่สำนักงาน • EMS ทั่วไทย ฿100 • DHL ทั่วโลก ฿2,500+", time: "1-7 วัน" },
];

const useCases = [
  { icon: HeartHandshake, title: "จดทะเบียนสมรสต่างประเทศ", desc: "แปล + รับรองสูติบัตร, บัตรประชาชน, ใบโสด ครบชุด พร้อมส่งสถานทูตปลายทาง", chips: ["ใบโสด", "ใบสำคัญสมรส", "Affidavit"] },
  { icon: GraduationCap, title: "ยื่นศึกษาต่อต่างประเทศ", desc: "Transcript + ปริญญาบัตร + Recommendation รับรองครบ NAATI/Sworn/MFA", chips: ["Transcript", "Diploma", "NAATI"] },
  { icon: Briefcase, title: "วีซ่าทำงาน Work Visa", desc: "Police Clearance + ประวัติการศึกษา + เอกสารการทำงาน รับรองครบสำหรับ Work Permit", chips: ["Police Clearance", "Work Record"] },
  { icon: Building2, title: "จดทะเบียนบริษัทต่างชาติ", desc: "หนังสือรับรอง + งบการเงิน + Power of Attorney แปลและรับรองส่งหน่วยงานสากล", chips: ["DBD", "POA", "Financial"] },
];

const trustBadges = [
  { icon: Landmark, label: "กระทรวงการต่างประเทศ", sub: "Consular MFA" },
  { icon: Scale, label: "สภาทนายความ", sub: "Notary License" },
  { icon: Globe2, label: "NAATI Australia", sub: "Certified Translator" },
  { icon: Building2, label: "กรมพัฒนาธุรกิจการค้า", sub: "DBD 0435567000061" },
  { icon: ShieldCheck, label: "Royal Thai Police", sub: "CID Clearance" },
];

const liveStats = [
  { k: "50+", v: "ภาษาที่ให้บริการ" },
  { k: "6", v: "ทนายผู้ทำคำรับรอง" },
  { k: "50+", v: "ภาษา" },
  { k: "DBD", v: "0435567000061" },
  { k: "6", v: "ทนาย Notary" },
  { k: "127", v: "พื้นที่บริการ" },
  { k: "15 นาที", v: "ตอบกลับ" },
  { k: "24 ชม.", v: "บริการด่วน" },
];


/* ---------- HELPERS ---------- */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-navy">
      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
      {children}
    </div>
  );
}

function SectionLabelDark({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
      {children}
    </div>
  );
}

/* ---------- HEADER ---------- */

function Header() {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "บริการ", href: "#services" },
    { label: "เอกสาร", href: "#documents" },
    { label: "ขั้นตอน", href: "#workflow" },
    { label: "ภาษา / พื้นที่", href: "#coverage" },
    { label: "ราคา", href: "#pricing" },
    { label: "ทีม Notary", href: "#trust" },
    { label: "FAQ", href: "#faq" },
  ];
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-navy-deep/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <a href="#top" className="flex items-center gap-3 text-white">
          <img src={nycLogo} alt="NYC Language Institute" width={44} height={44} className="h-11 w-11" loading="eager" fetchPriority="high" decoding="async" />
          <div className="leading-tight">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gold">สถาบันภาษาเอ็นวายซี</div>
            <div className="text-sm font-bold sm:text-base">NYC Language Institute</div>
          </div>
        </a>
        <nav className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <a key={l.label} href={l.href} className="text-sm font-medium text-white/80 transition hover:text-gold">
              {l.label}
            </a>
          ))}
          <a
            href="https://line.me/ti/p/@nycli"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep transition hover:bg-gold-soft"
          >
            <MessageCircle className="h-4 w-4" /> ขอใบเสนอราคา
          </a>
        </nav>
        <button
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="rounded-md border border-white/15 p-2 text-white lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-navy-deep px-4 pb-5 pt-3 lg:hidden">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-white/85 hover:bg-white/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-bold text-navy-deep"
            >
              <MessageCircle className="h-4 w-4" /> ขอใบเสนอราคา ฟรี
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- 1. HERO DASHBOARD ---------- */

/* Image with skeleton + fade-in, used for hero credential photos */
function CredImg({
  src,
  alt,
  width,
  height,
  className = "",
  imgClassName = "",
  eager = false,
  priority,
  skeletonClassName = "bg-white/10",
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  imgClassName?: string;
  eager?: boolean;
  priority?: "high" | "low" | "auto";
  skeletonClassName?: string;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    // If the image already finished loading before hydration attached onLoad,
    // imgRef.current.complete will be true — set loaded immediately.
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);
  return (
    <div className={`relative ${className}`}>
      {!loaded && (
        <div
          className={`absolute inset-0 animate-pulse rounded-[inherit] ${skeletonClassName}`}
          aria-hidden="true"
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`${imgClassName} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
}

function HeroDashboard() {
  return (
    <section
      id="top"
      aria-labelledby="hero-heading"
      className="relative overflow-hidden bg-navy-deep text-white"
    >
      {/* Background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: `linear-gradient(to bottom, oklch(0.18 0.07 260 / 0.85), oklch(0.18 0.07 260 / 0.98)), image-set(url(${heroEarth1280Avif}) type("image/avif") 1x, url(${heroEarth1920Avif}) type("image/avif") 2x, url(${heroEarth1280Webp}) type("image/webp") 1x, url(${heroEarth1920Webp}) type("image/webp") 2x, url(${heroEarth}))`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Grid pattern */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(0.92 0.12 92 / 1) 1px, transparent 1px), linear-gradient(90deg, oklch(0.92 0.12 92 / 1) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      {/* Glow orbs */}
      <div aria-hidden="true" className="pointer-events-none absolute -left-32 top-32 h-96 w-96 rounded-full bg-gold/20 blur-[120px]" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-navy-soft/40 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pb-28 lg:pt-14">
        {/* TOP: Headline + CTA (full width) */}
        <div className="max-w-4xl">
          <div className="flex flex-wrap gap-2 animate-fade-in">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gold sm:px-3.5 sm:text-[11px]">
              <Award className="h-3.5 w-3.5" /> จดทะเบียน DBD 0435567000061
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/85 sm:px-3.5 sm:text-[11px]">
              <Star className="h-3.5 w-3.5 fill-gold text-gold" aria-hidden="true" /> ทนายผู้ทำคำรับรอง 6 ท่าน
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/85 sm:px-3.5 sm:text-[11px]">
              <BadgeCheck className="h-3.5 w-3.5 text-gold" aria-hidden="true" /> 15+ ปี ประสบการณ์
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300 sm:px-3.5 sm:text-[11px]">
              <span aria-hidden="true" className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              Online · ตอบใน 15 นาที
            </span>
          </div>

          <h1
            id="hero-heading"
            className="mt-6 text-[32px] font-extrabold leading-[1.05] tracking-tight sm:mt-7 sm:text-5xl sm:leading-[1.02] lg:text-[64px]"
          >
            แปลภาษา · รับรองเอกสาร
            <br />
            <span className="bg-gradient-to-r from-gold via-gold-soft to-gold bg-clip-text text-transparent">
              ครบจบในที่เดียว
            </span>
          </h1>

          <p className="mt-4 max-w-2xl text-sm text-white/80 sm:mt-5 sm:text-base lg:text-lg">
            <strong className="text-white">NYC Legal &amp; Notary Service</strong> ผู้นำด้านบริการแปล 50+ ภาษา
            พร้อม <strong className="text-gold">Notary Public 6 ทนาย</strong>,
            NAATI Australia, รับรองกงสุล MFA และสถานทูตทุกประเทศ
            ครอบคลุม <strong className="text-gold">50 เขต กทม. + 77 จังหวัด</strong>
          </p>

          <div className="mt-6 flex flex-wrap gap-3 sm:mt-7">
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="ขอใบเสนอราคาฟรีผ่าน LINE @NYCLI ตอบกลับภายใน 15 นาที"
              className="group inline-flex min-h-11 items-center gap-2 rounded-xl bg-gold px-5 py-3 text-sm font-bold text-navy-deep shadow-[0_8px_30px_-8px] shadow-gold/60 transition hover:bg-gold-soft hover:shadow-gold/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep sm:px-6 sm:py-3.5"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              ขอใบเสนอราคา ฟรี · 15 นาที
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </a>
            <a
              href="#services"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white backdrop-blur transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep sm:px-6 sm:py-3.5"
            >
              <Globe className="h-4 w-4" aria-hidden="true" /> สำรวจบริการ
            </a>
          </div>
        </div>

        {/* HERO SHOWCASE IMAGE — Team & Services */}
        <figure className="relative mt-10 overflow-hidden rounded-3xl border border-gold/25 shadow-[0_30px_80px_-20px] shadow-navy-deep/80 animate-fade-in sm:mt-12">
          <div aria-hidden="true" className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-gold/40 via-transparent to-navy-soft/40 blur-2xl opacity-70" />
          <div className="relative">
            <img
              src={heroTeamShowcase}
              alt="ทีมทนายความและนักแปลของ NYC Legal & Notary Service กำลังตรวจสอบเอกสาร Apostille, หนังสือเดินทางไทย, สัญญาแปลหลายภาษา (ไทย, อังกฤษ, จีน, ญี่ปุ่น) ในสำนักงานย่านกรุงเทพฯ พร้อมตราชั่งแห่งความยุติธรรมและตราประทับ Notary Public"
              width={1920}
              height={1088}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="block h-auto w-full object-cover"
            />
            {/* Gradient scrim + caption */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy-deep/95 via-navy-deep/20 to-transparent" />
            <figcaption className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 p-4 sm:p-6 lg:p-8">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-navy-deep/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gold backdrop-blur sm:text-[11px]">
                  <Award className="h-3.5 w-3.5" aria-hidden="true" /> Editorial · ทีมงานตัวจริง
                </div>
                <p className="mt-2 text-sm font-semibold text-white drop-shadow sm:text-base lg:text-lg">
                  ทีมทนายความ Notary Public + นักแปลผู้เชี่ยวชาญ 50+ ภาษา — ตรวจเอกสารทุกฉบับก่อนส่งมอบ ยึดมั่นความถูกต้อง 100%
                </p>
              </div>
              <ul className="flex flex-wrap gap-2 text-[10px] sm:text-[11px]">
                {[
                  { icon: Award, label: "Notary Public" },
                  { icon: BadgeCheck, label: "Apostille · MFA" },
                  { icon: Globe, label: "NAATI · Embassy" },
                ].map(({ icon: Icon, label }) => (
                  <li key={label} className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-2.5 py-1 font-bold uppercase tracking-wider text-white backdrop-blur">
                    <Icon className="h-3 w-3 text-gold" aria-hidden="true" /> {label}
                  </li>
                ))}
              </ul>
            </figcaption>
          </div>
        </figure>

        {/* CREDENTIALS GRID: Trust Highlights (left) + 6 Attorneys (right) */}
        <div className="mt-10 grid gap-5 sm:mt-12 sm:gap-6 md:grid-cols-2 lg:gap-8">
          {/* LEFT: Trust Highlights */}
          <div className="group relative animate-fade-in">
            <div aria-hidden="true" className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-gold/30 via-gold/0 to-navy-soft/30 blur-2xl opacity-60 transition group-hover:opacity-100" />
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5 sm:py-3.5">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/85 sm:text-[11px]">
                  <Award className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
                  Why Choose NYC Legal
                </div>
                <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300 sm:px-2.5 sm:py-1 sm:text-[10px]">
                  Verified · DBD
                </span>
              </div>
              <ul className="flex-1 divide-y divide-white/10">
                {[
                  { k: "15+ ปี", v: "ประสบการณ์แปลและรับรองเอกสาร", sub: "ตั้งแต่ พ.ศ. 2553" },
                  { k: "50+ ภาษา", v: "แปลและรับรองคำแปลครบทุกภาษาสำคัญ", sub: "ใช้งานกับสถานทูตทุกประเทศ" },
                  { k: "127 พื้นที่", v: "50 เขต กทม. + 77 จังหวัดทั่วประเทศ", sub: "สำนักงานใหญ่ + 3 สาขา + ตัวแทน" },
                  { k: "15 นาที", v: "ตอบกลับและส่งใบเสนอราคาฟรี", sub: "ทุกวันจันทร์–เสาร์ 9:00–18:00" },
                ].map((it) => (
                  <li key={it.k} className="flex items-start gap-3 px-4 py-3 sm:px-5 sm:py-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-gold" aria-hidden="true" />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gold sm:text-base">{it.k}</div>
                      <div className="text-sm font-medium text-white">{it.v}</div>
                      <div className="text-[11px] text-white/80">{it.sub}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-white/10 bg-gradient-to-r from-navy via-navy-deep to-navy px-4 py-3 sm:px-5 sm:py-3.5">
                <div className="text-sm font-bold text-gold">บริษัท เอ็นวายซี ลีเกิล แอนด์ โนตารี เซอร์วิส จำกัด</div>
                <div className="mt-0.5 text-[11px] text-white/70">เลขทะเบียน 0435567000061 · จดทะเบียนถูกต้องตามกฎหมาย</div>
              </div>
            </div>
          </div>

          {/* RIGHT: 6 Licensed Attorneys */}
          <div className="group relative animate-fade-in">
            <div aria-hidden="true" className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-navy-soft/40 via-gold/10 to-gold/0 blur-2xl opacity-60 transition group-hover:opacity-100" />
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5 sm:py-3.5">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/85 sm:text-[11px]">
                  <CheckCircle2 className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
                  6 Licensed Attorneys
                </div>
                <span className="text-[10px] font-medium text-white/80">ใบอนุญาตตัวจริง</span>
              </div>
              {/* Grid of 6 */}
              <ul
                aria-label="ทนายความ Notary Public ที่ได้รับใบอนุญาตจากสภาทนายความ"
                className="grid flex-1 grid-cols-2 gap-2.5 p-3 sm:gap-3 sm:p-5 md:grid-cols-3"
              >
                {credentials.map((c) => (
                  <li key={c.title} className="contents">
                    <a
                      href={c.img}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`เปิดดูใบอนุญาตขนาดเต็ม: ${c.alt}`}
                      className="group/card flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white transition hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_12px_30px_-12px] hover:shadow-gold/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
                    >
                      <CredImg
                        src={c.img}
                        alt={c.alt}
                        width={300}
                        height={400}
                        priority="low"
                        skeletonClassName="bg-navy/10"
                        className="aspect-[3/4] w-full bg-white p-2"
                        imgClassName="h-full w-full object-contain"
                      />
                      <div className="border-t border-navy-deep/10 bg-gradient-to-r from-navy-deep to-navy px-2 py-1.5 text-center sm:px-2.5 sm:py-2">
                        <div className="truncate text-[11px] font-bold text-white sm:text-xs">{c.title}</div>
                        <div className="mt-0.5 truncate text-[9px] font-semibold uppercase tracking-wider text-gold sm:text-[10px]">
                          {c.badge}
                        </div>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
              {/* Footer hint */}
              <div className="border-t border-white/10 px-4 py-2.5 text-center text-[11px] text-white/80 sm:px-5 sm:py-3">
                ทุกท่านได้รับใบอนุญาตจากสภาทนายความแห่งประเทศไทย
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM STRIP: KPI + Quick Contact */}
        <div className="mt-8 grid gap-4 sm:mt-10 lg:grid-cols-[1fr_auto] lg:items-center">
          <ul aria-label="ตัวเลขสำคัญของบริษัท" className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
            {[
              { k: "50+", v: "ภาษา" },
              { k: "6", v: "Notary Public" },
              { k: "127", v: "เขต/จังหวัด" },
              { k: "6", v: "ทนายผู้ทำคำรับรอง" },
            ].map((s) => (
              <li key={s.v} className="rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur sm:p-4">
                <div className="text-xl font-extrabold text-gold sm:text-2xl lg:text-3xl">{s.k}</div>
                <div className="mt-1 text-[11px] text-white/70 sm:text-xs">{s.v}</div>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <a
              href="tel:0832494999"
              aria-label="โทร 083-249-4999"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white backdrop-blur transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <Phone className="h-4 w-4 text-gold" aria-hidden="true" /> 083-249-4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="แชทผ่าน LINE @NYCLI"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white backdrop-blur transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <MessageCircle className="h-4 w-4 text-gold" aria-hidden="true" /> @NYCLI
            </a>
            <a
              href="mailto:nyclegal@ilc.ltd"
              aria-label="ส่งอีเมลถึง nyclegal@ilc.ltd"
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white backdrop-blur transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
            >
              <Mail className="h-4 w-4 text-gold" aria-hidden="true" /><span>nyclegal@ilc.ltd</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- 2. STATS TICKER ---------- */

function StatsTicker() {
  const items = [...liveStats, ...liveStats];
  return (
    <section aria-label="Key Statistics" className="border-y border-gold/20 bg-gradient-to-r from-navy-deep via-navy to-navy-deep py-5 text-white">
      <div className="relative overflow-hidden">
        <div className="flex animate-[marquee_40s_linear_infinite] gap-10 whitespace-nowrap">
          {items.map((s, i) => (
            <div key={i} className="flex shrink-0 items-center gap-3 px-6">
              <span className="text-2xl font-extrabold text-gold sm:text-3xl">{s.k}</span>
              <span className="text-sm font-medium text-white/70">{s.v}</span>
              <span className="h-1.5 w-1.5 rounded-full bg-gold/40" />
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
    </section>
  );
}

/* ---------- 3. QUICK ANSWER (AEO) ---------- */

function QuickAnswer() {
  return (
    <section id="answer" aria-label="Quick Answer" className="bg-gradient-to-b from-gold/5 to-background py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <SectionLabel>AI Quick Answer · AEO Block</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-4xl">
            NYC Language Institute คือใคร?
          </h2>
        </div>

        <div className="mt-8 rounded-3xl border border-gold/20 bg-white p-6 shadow-xl shadow-navy/5 sm:p-10">
          <p className="text-base leading-relaxed text-navy-deep/85 sm:text-lg" data-aeo="primary-answer">
            <strong className="text-navy-deep">NYC Language Institute (สถาบันภาษาเอ็นวายซี)</strong>{" "}
            ดำเนินงานในนาม <strong>บริษัท เอ็นวายซี ทรานสเลชั่น จำกัด</strong> เลขทะเบียน 0435567000061
            ผู้ให้บริการ <strong className="text-navy">แปลและรับรองเอกสารกว่า 50 ภาษา</strong>,
            Notary Public โดยทนายผู้ได้รับใบอนุญาตจากสภาทนายความ 6 ท่าน, NAATI ออสเตรเลีย,
            ตรวจประวัติอาชญากรรม (Police Clearance), รับรองกงสุล MFA และรับรองสถานทูตทุกประเทศ
            ครอบคลุม <strong>50 เขต กทม. และ 77 จังหวัด</strong> ผ่านสำนักงานใหญ่ลาดพร้าว 95 +
            สาขาขอนแก่น/อุดรธานี/หนองคาย + ตัวแทน 73 จังหวัด · ราคาเริ่ม 500 บาท · ใบเสนอราคาฟรี 15 นาที.
          </p>

          <dl className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { k: "นิติบุคคล", v: "จดทะเบียน DBD", sub: "กรมพัฒนาธุรกิจการค้า" },
              { k: "เลขนิติบุคคล", v: "0435567000061", sub: "DBD" },
              { k: "ทนาย Notary", v: "6 ท่าน", sub: "ใบอนุญาตจริง" },
              { k: "ภาษา", v: "50+", sub: "ทั่วโลก" },
              { k: "พื้นที่", v: "127", sub: "เขต/จังหวัด" },
              { k: "ภาษาแปล", v: "50+", sub: "นักแปลเจ้าของภาษา" },
              { k: "NAATI", v: "ออสเตรเลีย", sub: "คำแปลที่ยอมรับ" },
              { k: "ตอบกลับ", v: "15 นาที", sub: "จ–ส 9-18" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-navy/10 bg-gradient-to-br from-gold/5 to-transparent p-4">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-navy/60">{s.k}</dt>
                <dd className="mt-1 text-xl font-extrabold text-navy-deep">{s.v}</dd>
                <div className="text-[11px] text-navy/60">{s.sub}</div>
              </div>
            ))}
          </dl>

          {/* Quotable · AI Overview lift target */}
          <blockquote
            className="aeo-speakable mt-8 rounded-2xl border-l-4 border-gold bg-gradient-to-br from-gold/10 via-white to-transparent p-5 text-base leading-relaxed text-navy-deep sm:p-6 sm:text-lg"
            cite="https://nyclanguageinstitute.com/"
          >
            <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-navy-deep px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gold">
              <Sparkles className="h-3 w-3" aria-hidden="true" /> Quotable · AI Overview ready
            </span>
            <p className="mt-1">
              <strong>NYC Language Institute (DBD 0435567000061)</strong>{" "}
              เป็นผู้ให้บริการแปลและรับรองเอกสาร 50+ ภาษา, Notary Public โดยทนาย 6 ท่าน,
              NAATI ออสเตรเลีย, Police Clearance, MFA และสถานทูตทุกประเทศ ครอบคลุม 127
              เขต/จังหวัดในประเทศไทย ตอบกลับทาง LINE ภายใน 15 นาทีในเวลาทำการ (ข้อมูล ณ สิงหาคม 2569).
            </p>
            <footer className="mt-2 text-xs text-navy/60">
              ที่มา: nyclanguageinstitute.com · DBD · สภาทนายความฯ
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

/* ---------- 4. SERVICE BENTO ---------- */

function ServiceBento() {
  return (
    <section id="services" className="bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <SectionLabel>Core Services · 6 Departments</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
            บริการครบวงจร <span className="text-gold">ครอบคลุมทุกความต้องการ</span>
          </h2>
          <p className="mt-4 text-base text-navy/70 sm:text-lg">
            ตั้งแต่แปลเอกสารราชการไปจนถึงรับรองนิติกรณ์สถานทูตปลายทาง — เลือกบริการที่ใช่ได้ในที่เดียว
          </p>
        </div>

        <div className="mt-10 grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {departments.map((d, i) => {
            const isFeatured = i === 0;
            return (
              <a
                key={d.title}
                href={d.siteUrl}
                target="_blank"
                rel="noreferrer noopener"
                className={`group relative overflow-hidden rounded-3xl border border-navy/10 bg-white p-6 transition hover:-translate-y-1 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/10 sm:p-7 ${
                  isFeatured ? "sm:col-span-2 lg:row-span-2 bg-gradient-to-br from-navy-deep to-navy text-white hover:border-gold" : ""
                }`}
              >
                {/* Decorative glow */}
                <div className={`absolute -right-12 -top-12 h-40 w-40 rounded-full blur-3xl transition group-hover:scale-150 ${isFeatured ? "bg-gold/30" : "bg-gold/10"}`} />

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${isFeatured ? "bg-gold text-navy-deep" : "bg-gold/10 text-navy-deep"}`}>
                      <d.icon className="h-6 w-6" />
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${isFeatured ? "bg-white/15 text-white" : "bg-navy/5 text-navy"}`}>
                      {d.tag}
                    </span>
                  </div>

                  <h3 className={`mt-5 text-xl font-extrabold sm:text-2xl ${isFeatured ? "text-white" : "text-navy-deep"}`}>
                    {d.title}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed ${isFeatured ? "text-white/75" : "text-navy/65"}`}>
                    {d.desc}
                  </p>

                  {isFeatured && (
                    <ul className="mt-5 grid gap-2 text-sm text-white/85">
                      {["แปล 50+ ภาษา รับรองนักแปล", "ส่งกงสุล MFA + สถานทูตทุกประเทศ", "ใบเสนอราคาฟรี 15 นาที", "บริการด่วน 24 ชม."].map((x) => (
                        <li key={x} className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-gold" /> {x}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className={`mt-6 flex items-center justify-between border-t pt-4 ${isFeatured ? "border-white/15" : "border-navy/10"}`}>
                    <div>
                      <div className={`text-[10px] font-bold uppercase tracking-wider ${isFeatured ? "text-white/80" : "text-navy/55"}`}>
                        เริ่มต้น
                      </div>
                      <div className={`text-lg font-extrabold ${isFeatured ? "text-gold" : "text-navy-deep"}`}>
                        {d.startPrice}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${isFeatured ? "text-gold" : "text-navy"}`}>
                      LINE {d.line} <ExternalLink className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- 5. DOCUMENT UNIVERSE ---------- */

function DocumentUniverse() {
  const [active, setActive] = useState(documentCategories[0].id);
  const current = documentCategories.find((c) => c.id === active) ?? documentCategories[0];
  return (
    <section id="documents" className="bg-navy-deep py-20 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <SectionLabelDark>Document Universe · 50,000+ Documents</SectionLabelDark>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-5xl">
            แปลและรับรอง <span className="text-gold">เอกสารทุกประเภท</span>
          </h2>
          <p className="mt-4 text-base text-white/70 sm:text-lg">
            แบ่งหมวดชัดเจน รู้ราคาและระยะเวลาก่อนเริ่มงาน — เลือกหมวดเอกสารของคุณเพื่อดูรายละเอียด
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-2">
          {documentCategories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition ${
                active === c.id
                  ? "border-gold bg-gold text-navy-deep"
                  : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
              }`}
            >
              <c.icon className="h-4 w-4" />
              {c.label}
            </button>
          ))}
        </div>

        {/* Panel */}
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur sm:p-7 animate-fade-in" key={active}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {current.items.map((it) => (
                <div key={it.label} className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3.5 transition hover:border-gold/40 hover:bg-white/[0.06]">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gold/15 text-gold">
                    <it.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-semibold text-white/90">{it.label}</span>
                </div>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-3">
            <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/15 to-transparent p-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gold/90">ราคาเริ่มต้น</div>
              <div className="mt-1 text-2xl font-extrabold text-white">{current.price}</div>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/[0.03] p-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/80">ระยะเวลา</div>
              <div className="mt-1 text-2xl font-extrabold text-white">{current.eta}</div>
              <div className="mt-1 text-xs text-white/80">บริการด่วน 24 ชม. มีให้บริการ</div>
            </div>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gold px-5 py-3.5 text-sm font-bold text-navy-deep transition hover:bg-gold-soft"
            >
              <MessageCircle className="h-4 w-4" /> สอบถามหมวดนี้
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}

/* ---------- 6. WORKFLOW TIMELINE ---------- */

function WorkflowTimeline() {
  return (
    <section id="workflow" className="bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <SectionLabel>5-Step Workflow</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
            ขั้นตอนการให้บริการ <span className="text-gold">ง่าย โปร่งใส รวดเร็ว</span>
          </h2>
        </div>

        {/* Desktop: horizontal */}
        <div className="mt-12 hidden lg:block">
          <div className="relative">
            <div className="absolute left-0 right-0 top-7 h-1 rounded-full bg-gradient-to-r from-gold/20 via-gold to-gold/20" />
            <div className="relative grid grid-cols-5 gap-4">
              {workflowSteps.map((s, i) => (
                <div key={s.title} className="flex flex-col items-center text-center">
                  <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-4 border-background bg-gold text-navy-deep shadow-lg shadow-gold/30">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-navy-deep/5 px-2.5 py-0.5 text-[10px] font-bold text-navy-deep">
                    <span className="font-mono">STEP {i + 1}</span>
                    <span className="text-navy/40">·</span>
                    <span>{s.time}</span>
                  </div>
                  <h3 className="mt-3 text-base font-extrabold text-navy-deep">{s.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-navy/65">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: vertical */}
        <div className="mt-10 space-y-3 lg:hidden">
          {workflowSteps.map((s, i) => (
            <div key={s.title} className="flex gap-4 rounded-2xl border border-navy/10 bg-white p-4">
              <div className="flex flex-col items-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold text-navy-deep">
                  <s.icon className="h-5 w-5" />
                </div>
                {i < workflowSteps.length - 1 && <div className="mt-1 w-px flex-1 bg-gold/30" />}
              </div>
              <div className="flex-1 pb-2">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-navy/60">
                  <span className="font-mono">STEP {i + 1}</span>
                  <span>·</span>
                  <span className="text-gold">{s.time}</span>
                </div>
                <h3 className="mt-1 text-base font-extrabold text-navy-deep">{s.title}</h3>
                <p className="mt-1 text-sm text-navy/65">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 7. COVERAGE HUB ---------- */

function CoverageHub() {
  const tabs = [
    { id: "lang", label: "50+ ภาษา", icon: Languages, count: "50+" },
    { id: "bkk", label: "Bangkok 50 เขต", icon: Building2, count: "50" },
    { id: "prov", label: "77 จังหวัด", icon: MapPin, count: "77" },
  ] as const;
  const [tab, setTab] = useState<(typeof tabs)[number]["id"]>("lang");
  const [query, setQuery] = useState("");

  const filteredBkk = useMemo(
    () => bkkDistricts.filter((d) => d.includes(query.trim())),
    [query]
  );
  const filteredProv = useMemo(
    () => provincesAll.filter((p) => p.includes(query.trim())),
    [query]
  );

  return (
    <section id="coverage" className="bg-gradient-to-b from-background to-gold/5 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <SectionLabel>Language & Coverage</SectionLabel>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
              <span className="text-gold">50+ ภาษา</span> · 127 พื้นที่ทั่วไทย
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              สำนักงานใหญ่ลาดพร้าว + 3 สาขาภูมิภาค + เครือข่ายตัวแทน 73 จังหวัด
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setQuery(""); }}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition ${
                  tab === t.id
                    ? "border-navy-deep bg-navy-deep text-white"
                    : "border-navy/15 bg-white text-navy hover:bg-navy/5"
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${tab === t.id ? "bg-gold text-navy-deep" : "bg-gold/15 text-navy"}`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-3xl border border-navy/10 bg-white p-5 shadow-xl shadow-navy/5 sm:p-7">
          {/* Language Tab */}
          {tab === "lang" && (
            <div className="grid gap-4 sm:grid-cols-2">
              {languagesByRegion.map((r) => (
                <div key={r.region} className="rounded-2xl border border-navy/10 bg-gradient-to-br from-gold/5 to-transparent p-5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy-deep text-gold">
                      <r.icon className="h-4 w-4" />
                    </div>
                    <h3 className="text-base font-extrabold text-navy-deep">{r.region}</h3>
                    <span className="ml-auto text-xs font-bold text-navy/55">{r.langs.length} ภาษา</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {r.langs.map((l) => (
                      <span key={l} className="rounded-full border border-navy/10 bg-white px-2.5 py-1 text-xs font-medium text-navy/80">
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bangkok Tab */}
          {tab === "bkk" && (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ค้นหาเขตในกรุงเทพ..."
                  aria-label="ค้นหาเขตในกรุงเทพมหานครที่ให้บริการ"
                  className="w-full rounded-xl border border-navy/15 bg-background py-3 pl-10 pr-4 text-sm outline-none focus:border-gold"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {filteredBkk.map((d) => (
                  <span key={d} className="rounded-full border border-navy/10 bg-gradient-to-br from-gold/10 to-transparent px-3 py-1.5 text-xs font-medium text-navy">
                    {d}
                  </span>
                ))}
                {filteredBkk.length === 0 && (
                  <span className="text-sm text-navy/55">ไม่พบเขตที่ค้นหา</span>
                )}
              </div>
            </>
          )}

          {/* Provinces Tab */}
          {tab === "prov" && (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ค้นหาจังหวัด..."
                  aria-label="ค้นหาจังหวัดที่ให้บริการ"
                  className="w-full rounded-xl border border-navy/15 bg-background py-3 pl-10 pr-4 text-sm outline-none focus:border-gold"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {filteredProv.map((p) => (
                  <span key={p} className="rounded-full border border-navy/10 bg-white px-3 py-1.5 text-xs font-medium text-navy">
                    {p}
                  </span>
                ))}
                {filteredProv.length === 0 && (
                  <span className="text-sm text-navy/55">ไม่พบจังหวัดที่ค้นหา</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Branches strip */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {branches.map((b) => (
            <div key={b.name} className="rounded-2xl border border-navy/10 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gold">{b.tag}</div>
              <div className="mt-1 text-base font-extrabold text-navy-deep">{b.name}</div>
              <div className="mt-1 flex items-start gap-1.5 text-xs text-navy/65">
                <MapPin className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" />
                <span>{b.addr}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 8. PRICING HUB ---------- */

function PricingHub() {
  const tabs = [
    { id: "translation", label: "แปลเอกสาร", icon: FileText },
    { id: "notary", label: "Notary & กงสุล", icon: Stamp },
    { id: "background", label: "ตรวจประวัติ + ส่ง", icon: ShieldCheck },
  ] as const;
  const [tab, setTab] = useState<(typeof tabs)[number]["id"]>("translation");
  const rows = pricing[tab];

  return (
    <section id="pricing" className="bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <SectionLabel>Transparent Pricing</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
            ราคามาตรฐาน <span className="text-gold">โปร่งใส ไม่มีค่าซ่อน</span>
          </h2>
          <p className="mt-4 text-base text-navy/70 sm:text-lg">
            ใบเสนอราคาฟรีภายใน 15 นาที · ออกใบกำกับภาษี VAT · รับโอน/พร้อมเพย์/บัตรเครดิต
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition ${
                tab === t.id
                  ? "border-navy-deep bg-navy-deep text-white"
                  : "border-navy/15 bg-white text-navy hover:bg-navy/5"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2" key={tab}>
          {rows.map((r) => (
            <div key={r.name} className="group rounded-2xl border border-navy/10 bg-white p-5 transition hover:border-gold/40 hover:shadow-lg hover:shadow-gold/10">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h3 className="text-base font-extrabold text-navy-deep">{r.name}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-navy/60">{r.note}</p>
                </div>
                <div className="text-right">
                  <div className="text-base font-extrabold text-gold sm:text-lg">{r.price}</div>
                  <div className="text-[10px] font-medium text-navy/55">{r.unit}</div>
                </div>
              </div>
              <a
                href="https://line.me/ti/p/@nycli"
                target="_blank"
                rel="noreferrer noopener"
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-navy-deep/15 bg-navy-deep/[0.03] py-2.5 text-xs font-bold text-navy-deep transition group-hover:bg-gold group-hover:text-navy-deep"
              >
                <MessageCircle className="h-3.5 w-3.5" /> ขอใบเสนอราคา
              </a>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col items-center justify-between gap-4 rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/15 via-gold/5 to-gold/15 p-6 sm:flex-row">
          <div className="flex items-center gap-3 text-navy-deep">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-navy-deep">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-extrabold">ใบเสนอราคาฟรีภายใน 15 นาที</div>
              <div className="text-xs text-navy/65">ส่งสำเนาเอกสารผ่าน LINE ได้เลย</div>
            </div>
          </div>
          <a
            href="https://line.me/ti/p/@nycli"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-xl bg-navy-deep px-5 py-3 text-sm font-bold text-white transition hover:bg-navy"
          >
            <MessageCircle className="h-4 w-4" /> LINE @NYCLI
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------- 9. TRUST HUB ---------- */

function TrustHub() {
  return (
    <section id="trust" className="bg-navy-deep py-20 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <SectionLabelDark>Trust · Credentials · Team</SectionLabelDark>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-5xl">
            จดทะเบียนถูกต้อง · <span className="text-gold">รับรองครบวงจร</span>
          </h2>
        </div>

        {/* Trust badges */}
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {trustBadges.map((b) => (
            <div key={b.label} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center backdrop-blur">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold">
                <b.icon className="h-6 w-6" />
              </div>
              <div className="mt-3 text-sm font-extrabold text-white">{b.label}</div>
              <div className="mt-1 text-[10px] font-medium uppercase tracking-wider text-white/80">{b.sub}</div>
            </div>
          ))}
        </div>

        {/* About + Team grid */}
        <div className="mt-10 grid gap-6 lg:grid-cols-12">
          {/* About */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold">
              <Building2 className="h-3.5 w-3.5" /> About Company
            </div>
            <h3 className="mt-4 text-2xl font-extrabold text-white">
              บริษัท เอ็นวายซี ทรานสเลชั่น จำกัด
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75">
              ก่อตั้งปี พ.ศ. 2553 · จดทะเบียน DBD เลขนิติบุคคล <strong className="text-gold">0435567000061</strong> ·
              ดำเนินธุรกิจการแปลและรับรองเอกสารระดับมืออาชีพมากว่า 15 ปี โดยทีมนักแปลเฉพาะทางและทนายความ Notary Public
              ผู้ได้รับใบอนุญาตจริงจากสภาทนายความ
            </p>
            <dl className="mt-5 grid grid-cols-2 gap-3">
              {[
                { k: "ก่อตั้ง", v: "พ.ศ. 2553" },
                { k: "DBD", v: "0435567000061" },
                { k: "ที่ตั้ง", v: "ลาดพร้าว 95" },
                { k: "VAT", v: "จดทะเบียน" },
              ].map((s) => (
                <div key={s.k} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-white/80">{s.k}</dt>
                  <dd className="mt-1 text-sm font-bold text-gold">{s.v}</dd>
                </div>
              ))}
            </dl>
            <a
              href="https://datawarehouse.dbd.go.th/"
              target="_blank"
              rel="noreferrer noopener"
              className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-gold hover:text-gold-soft"
            >
              ตรวจสอบที่ DBD <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* Team */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur lg:col-span-7">
            <div className="flex items-center justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold">
                  <Scale className="h-3.5 w-3.5" /> Notary Public Team
                </div>
                <h3 className="mt-4 text-2xl font-extrabold text-white">
                  ทนาย Notary Public <span className="text-gold">6 ท่าน</span>
                </h3>
              </div>
              <div className="hidden text-right sm:block">
                <div className="text-3xl font-extrabold text-gold">6</div>
                <div className="text-[10px] uppercase tracking-wider text-white/80">ใบอนุญาตจริง</div>
              </div>
            </div>
            <div className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {attorneys.map((a) => (
                <div key={a.license} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gold/15 text-gold">
                      <Scale className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-white">{a.th}</div>
                      <div className="truncate text-[11px] text-white/80">{a.en}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-white/80">
                    <span className="font-mono">No. {a.license}</span>
                    <span>ตั้งแต่ {a.since}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- 10. TESTIMONIALS MARQUEE ---------- */

function TestimonialsMarquee() {
  const half = Math.ceil(testimonials.length / 2);
  const row1 = [...testimonials.slice(0, half), ...testimonials.slice(0, half)];
  const row2 = [...testimonials.slice(half), ...testimonials.slice(half)];
  return (
    <section id="testimonials" className="overflow-hidden bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
        <SectionLabel>เสียงจากลูกค้า</SectionLabel>
        <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
          เสียงจริงจาก<span className="text-gold">ลูกค้าที่ใช้บริการ</span>
        </h2>
      </div>

      <div className="mt-10 space-y-4">
        <div className="relative">
          <div className="flex animate-[marquee_60s_linear_infinite] gap-4">
            {row1.map((t, i) => (
              <TestimonialCard key={`r1-${i}`} t={t} />
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="flex animate-[marquee-rev_70s_linear_infinite] gap-4">
            {row2.map((t, i) => (
              <TestimonialCard key={`r2-${i}`} t={t} />
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee-rev { from { transform: translateX(-50%) } to { transform: translateX(0) } }
      `}</style>
    </section>
  );
}

function TestimonialCard({ t }: { t: (typeof testimonials)[number] }) {
  return (
    <div className="w-[320px] flex-none rounded-2xl border border-navy/10 bg-white p-5 shadow-sm sm:w-[360px]">
      <div className="flex items-center gap-1 text-gold">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="h-3.5 w-3.5 fill-gold" />
        ))}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-navy/85 line-clamp-3">{t.text}</p>
      <div className="mt-4 flex items-center justify-between border-t border-navy/10 pt-3">
        <div className="text-xs font-bold text-navy-deep">{t.name}</div>
        <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-navy">{t.service}</span>
      </div>
    </div>
  );
}

/* ---------- 11. USE CASES ---------- */

function UseCases() {
  return (
    <section id="usecases" className="bg-gradient-to-b from-gold/5 to-background py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <SectionLabel>Real Use Cases</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
            สถานการณ์ที่ลูกค้าใช้บริการบ่อย
          </h2>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {useCases.map((u) => (
            <div key={u.title} className="group rounded-3xl border border-navy/10 bg-white p-6 transition hover:-translate-y-1 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/10 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-navy-deep to-navy text-gold">
                  <u.icon className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-extrabold text-navy-deep sm:text-xl">{u.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/65">{u.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {u.chips.map((c) => (
                      <span key={c} className="rounded-full bg-gold/15 px-2.5 py-1 text-[11px] font-bold text-navy">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 12. FAQ ---------- */

function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => faqs.filter((f) => f.q.includes(q.trim()) || f.a.includes(q.trim())),
    [q]
  );
  return (
    <section id="faq" className="bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="text-center">
          <SectionLabel>FAQ · คำถามที่พบบ่อย</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-deep sm:text-5xl">
            มีคำถาม? <span className="text-gold">เรามีคำตอบ</span>
          </h2>
        </div>

        <div className="relative mx-auto mt-8 max-w-2xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ค้นหาคำถาม เช่น ราคา, NAATI, Notary..."
            aria-label="ค้นหาคำถามที่พบบ่อย"
            className="w-full rounded-full border border-navy/15 bg-white py-3.5 pl-11 pr-4 text-sm outline-none focus:border-gold"
          />
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {filtered.map((f, i) => (
            <button
              key={f.q}
              onClick={() => setOpen(open === i ? null : i)}
              className="rounded-2xl border border-navy/10 bg-white p-5 text-left transition hover:border-gold/40"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-extrabold text-navy-deep">{f.q}</h3>
                <ChevronDown className={`h-4 w-4 flex-none text-navy/40 transition ${open === i ? "rotate-180 text-gold" : ""}`} />
              </div>
              {open === i && (
                <p className="mt-3 text-sm leading-relaxed text-navy/70 animate-fade-in">{f.a}</p>
              )}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="col-span-full text-center text-sm text-navy/55">ไม่พบคำถามที่ค้นหา · สอบถามทาง LINE ได้เลย</p>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- 13. FINAL CTA ---------- */

function FinalCTA() {
  return (
    <section aria-label="Final CTA" className="relative overflow-hidden bg-navy-deep py-20 text-white sm:py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-gold/30 blur-[120px]" />
        <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-gold/20 blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6">
        <SectionLabelDark>Ready to Start?</SectionLabelDark>
        <h2 className="mt-5 text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
          พร้อมเริ่ม? <span className="text-gold">ใบเสนอราคาฟรีใน 15 นาที</span>
        </h2>
        <p className="mt-5 text-base text-white/75 sm:text-lg">
          ส่งสำเนาเอกสารทาง LINE หรือ Email ทีมงานจะตอบกลับทันที · จ–ส 9:00–18:00
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://line.me/ti/p/@nycli"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-xl bg-gold px-7 py-4 text-sm font-bold text-navy-deep shadow-[0_10px_40px_-10px] shadow-gold/60 transition hover:bg-gold-soft sm:text-base"
          >
            <MessageCircle className="h-5 w-5" /> LINE @NYCLI
          </a>
          <a
            href="tel:0832494999"
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-4 text-sm font-bold text-white backdrop-blur transition hover:bg-white/10 sm:text-base"
          >
            <Phone className="h-5 w-5" /> 083-2494999
          </a>
          <a
            href="mailto:nycli@ilc.ltd"
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-4 text-sm font-bold text-white backdrop-blur transition hover:bg-white/10 sm:text-base"
          >
            <Mail className="h-5 w-5" /><span>nycli@ilc.ltd</span>
          </a>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/80">
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold" /> จ–ส 9:00–18:00</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> ลาดพร้าว 95, กทม.</span>
          <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-gold" /> DBD 0435567000061</span>
        </div>
      </div>
    </section>
  );
}

/* ---------- STICKY MOBILE CTA ---------- */

function StickyCTA() {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-white/10 bg-navy-deep/95 backdrop-blur lg:hidden">
      <a href="tel:0832494999" className="flex flex-1 items-center justify-center gap-1.5 py-3.5 text-xs font-bold text-white">
        <Phone className="h-4 w-4 text-gold" /> โทร
      </a>
      <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer noopener" className="flex flex-1 items-center justify-center gap-1.5 bg-gold py-3.5 text-xs font-bold text-navy-deep">
        <MessageCircle className="h-4 w-4" /> LINE @NYCLI
      </a>
      <a href="mailto:nycli@ilc.ltd" className="flex flex-1 items-center justify-center gap-1.5 py-3.5 text-xs font-bold text-white">
        <Mail className="h-4 w-4 text-gold" /> Email
      </a>
    </div>
  );
}

/* ---------- INDEX ---------- */

function Index() {
  return (
    <div className="min-h-screen bg-background pb-16 lg:pb-0">
      <Header />
      <main>
        <HeroDashboard />
        <StatsTicker />
        <QuickAnswer />
        <ServiceBento />
        <DocumentUniverse />
        <WorkflowTimeline />
        <CoverageHub />
        <section aria-label="Near me" className="bg-background py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <NearMeWidget />
          </div>
        </section>
        <PricingHub />
        <TrustHub />
        <LazyHydrate minHeight={520} rootMargin="500px">
          <TestimonialsMarquee />
        </LazyHydrate>
        <LazyHydrate minHeight={620} rootMargin="500px">
          <UseCases />
        </LazyHydrate>
        <LazyHydrate minHeight={640} rootMargin="500px">
          <FAQ />
        </LazyHydrate>
        <FinalCTA />
        <LazyHydrate minHeight={600} rootMargin="600px">
          <InternalSiteMap />
        </LazyHydrate>

            <SiteCoreLinks />
    </main>
      <StickyCTA />
    </div>
  );
}

export default Index;
